import type { OrgFieldConfig } from "@/api/org_field_config.tsx"
import { FieldType } from "@/api/org_field_config.tsx"

export type DateFilterTableContext = "deduction_table" | "accounting_table" | "check_detail_table"

/** Maps the current route to table contexts used for posting-date filter gating. */
export function date_filter_table_contexts(pathname: string): DateFilterTableContext[] {
  if (pathname.startsWith("/deductions")) {
    return ["deduction_table"]
  }
  if (pathname.startsWith("/transactions")) {
    return ["accounting_table"]
  }
  if (pathname.startsWith("/accounting/checks")) {
    return ["check_detail_table", "accounting_table"]
  }
  return []
}

/** Whether the org has posting_date enabled for the given table context. */
export function posting_date_filter_enabled(
  configs: OrgFieldConfig[],
  tableContext: DateFilterTableContext
): boolean {
  return configs.some(
    config =>
      config.is_active &&
      config.field_name === "posting_date" &&
      config.table_contexts.includes(tableContext)
  )
}

/**
 * Whether "Deposit Date" (the payment date entered before a check is pushed to the accounting
 * system) should appear in the global date filter on this route: only the accounting lists, where
 * that date is what ties a deduction to the month it actually hit the books.
 */
export function deposit_date_filter_available(pathname: string): boolean {
  return date_filter_table_contexts(pathname).includes("accounting_table")
}

/** Whether posting date should appear in the global date filter on this route. */
export function posting_date_filter_available(pathname: string, configs: OrgFieldConfig[]): boolean {
  return date_filter_table_contexts(pathname).some(context =>
    posting_date_filter_enabled(configs, context)
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const posting_config: OrgFieldConfig = {
    id: "1",
    org_id: "org",
    field_name: "posting_date",
    field_type: FieldType.Date,
    display_name: "Posting Date",
    display_order: 0,
    is_active: true,
    table_contexts: ["deduction_table", "accounting_table"],
    created_at: "",
    updated_at: "",
  }

  describe("date_filter_table_contexts", () => {
    it("maps deductions routes", () => {
      expect(date_filter_table_contexts("/deductions")).toEqual(["deduction_table"])
      expect(date_filter_table_contexts("/deductions/won-pending-payment")).toEqual(["deduction_table"])
    })

    it("maps transactions routes", () => {
      expect(date_filter_table_contexts("/transactions")).toEqual(["accounting_table"])
      expect(date_filter_table_contexts("/transactions/deductions")).toEqual(["accounting_table"])
    })

    it("maps cash application routes", () => {
      expect(date_filter_table_contexts("/accounting/checks")).toEqual([
        "check_detail_table",
        "accounting_table",
      ])
    })

    it("returns empty for other routes", () => {
      expect(date_filter_table_contexts("/dashboard")).toEqual([])
    })
  })

  describe("deposit_date_filter_available", () => {
    it("is offered on the accounting lists only", () => {
      expect(deposit_date_filter_available("/transactions")).toBe(true)
      expect(deposit_date_filter_available("/accounting/checks")).toBe(true)
      expect(deposit_date_filter_available("/deductions")).toBe(false)
      expect(deposit_date_filter_available("/dashboard")).toBe(false)
    })
  })

  describe("posting_date_filter_enabled", () => {
    it("is true when active for the table context", () => {
      expect(posting_date_filter_enabled([posting_config], "deduction_table")).toBe(true)
    })

    it("is false when inactive", () => {
      expect(
        posting_date_filter_enabled([{ ...posting_config, is_active: false }], "deduction_table")
      ).toBe(false)
    })

    it("is false when context does not match", () => {
      expect(
        posting_date_filter_enabled(
          [{ ...posting_config, table_contexts: ["accounting_table"] }],
          "deduction_table"
        )
      ).toBe(false)
    })
  })

  describe("posting_date_filter_available", () => {
    it("is true on deductions when enabled for deduction_table", () => {
      expect(posting_date_filter_available("/deductions", [posting_config])).toBe(true)
    })

    it("is true on cash application when enabled for check_detail_table", () => {
      expect(
        posting_date_filter_available("/accounting/checks", [
          { ...posting_config, table_contexts: ["check_detail_table"] },
        ])
      ).toBe(true)
    })

    it("is false on dashboard even when org has posting_date", () => {
      expect(posting_date_filter_available("/dashboard", [posting_config])).toBe(false)
    })
  })
}
